/**
 * Creative Library tab — manage ad creatives: list, create, preview, delete.
 *
 * Supports both Meta creatives (image + text + CTA) and Google Ads assets
 * (headlines, descriptions for responsive search ads).
 */

import { useEffect, useState, useCallback } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Spinner,
} from '@/components/ui'
import { Badge } from '@/components/ui'
import { Button } from '@/components/ui'
import { ChannelBadge } from '@/components/marketing/ChannelBadge'
import {
  Plus,
  Trash2,
  Copy,
  Image as ImageIcon,
  RefreshCw,
  X,
} from 'lucide-react'
import { useMarketing } from '@/hooks/useMarketing'
import { useToast } from '@/components/ui/Toast'
import type { Creative, CreativeCreateRequest, CreativeVariationCreateRequest } from '@/types/marketing'

export function CreativeLibrary() {
  const {
    fetchStatus,
    fetchCreatives,
    createCreative,
    createVariation,
    deleteCreative,
    hasAccount,
    creatives,
  } = useMarketing()

  const { addToast } = useToast()
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showVariationForm, setShowVariationForm] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  // Create form state
  const [createName, setCreateName] = useState('')
  const [createHeadline, setCreateHeadline] = useState('')
  const [createBody, setCreateBody] = useState('')
  const [createCta, setCreateCta] = useState('LEARN_MORE')
  const [creating, setCreating] = useState(false)

  // Variation form state
  const [varName, setVarName] = useState('')
  const [varHeadline, setVarHeadline] = useState('')
  const [varBody, setVarBody] = useState('')
  const [varCta, setVarCta] = useState('LEARN_MORE')

  const CTA_OPTIONS = [
    'LEARN_MORE',
    'SHOP_NOW',
    'SIGN_UP',
    'WHATSAPP_MESSAGE',
    'GET_QUOTE',
    'CONTACT_US',
  ]

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      const status = await fetchStatus()
      if (status.hasAccount) {
        await fetchCreatives()
      }
    } catch {
      // silently handle
    } finally {
      setLoading(false)
    }
  }, [fetchStatus, fetchCreatives])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const handleCreate = async () => {
    if (!createName.trim()) return
    setCreating(true)
    try {
      const data: CreativeCreateRequest = {
        name: createName.trim(),
        headline: createHeadline.trim() || undefined,
        bodyText: createBody.trim() || undefined,
        callToAction: createCta,
      }
      await createCreative(data)
      addToast({ title: 'Creative created', description: data.name, type: 'success' })
      setShowCreateForm(false)
      setCreateName('')
      setCreateHeadline('')
      setCreateBody('')
      setCreateCta('LEARN_MORE')
    } catch (err) {
      addToast({ title: 'Error', description: String(err), type: 'error' })
    } finally {
      setCreating(false)
    }
  }

  const handleCreateVariation = async (parentId: string) => {
    if (!varName.trim()) return
    setCreating(true)
    try {
      const data: CreativeVariationCreateRequest = {
        name: varName.trim(),
        headline: varHeadline.trim() || undefined,
        bodyText: varBody.trim() || undefined,
        callToAction: varCta,
      }
      await createVariation(parentId, data)
      addToast({ title: 'Variation created', description: data.name, type: 'success' })
      setShowVariationForm(null)
      setVarName('')
      setVarHeadline('')
      setVarBody('')
      setVarCta('LEARN_MORE')
    } catch (err) {
      addToast({ title: 'Error', description: String(err), type: 'error' })
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (creative: Creative) => {
    setDeleting(creative.id)
    try {
      await deleteCreative(creative.id)
      addToast({ title: 'Creative deleted', description: creative.name, type: 'success' })
    } catch (err) {
      addToast({ title: 'Error', description: String(err), type: 'error' })
    } finally {
      setDeleting(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!hasAccount) {
    return (
      <Card>
        <CardContent className="py-12 text-center space-y-3">
          <p className="text-muted-foreground">No Meta Ad Account connected.</p>
          <p className="text-sm text-muted-foreground">
            Go to the <strong>Settings</strong> tab to connect your Meta Ad Account.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{creatives.length} creative{creatives.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={loadAll}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreateForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Creative
          </Button>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>New Creative</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowCreateForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Name *</label>
              <input
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g., Spring Sale Banner"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Headline</label>
              <input
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={createHeadline}
                onChange={(e) => setCreateHeadline(e.target.value)}
                placeholder="e.g., Fresh Baked Daily"
                maxLength={255}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Body Text</label>
              <textarea
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={createBody}
                onChange={(e) => setCreateBody(e.target.value)}
                placeholder="Ad body text..."
                rows={3}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Call to Action</label>
              <select
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={createCta}
                onChange={(e) => setCreateCta(e.target.value)}
              >
                {CTA_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setShowCreateForm(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} disabled={creating || !createName.trim()}>
                {creating && <Spinner className="w-3 h-3 mr-1" />}
                Create
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Creative Grid */}
      {creatives.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ImageIcon className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No creatives yet. Create your first ad creative above.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {creatives.map((creative) => (
            <Card key={creative.id} className="overflow-hidden p-0 gap-0">
              {/* Thumbnail */}
              {creative.thumbnailUrl || creative.blobUrl ? (
                <div className="aspect-video bg-muted overflow-hidden">
                  <img
                    src={creative.thumbnailUrl || creative.blobUrl}
                    alt={creative.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-video bg-muted flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-muted-foreground" />
                </div>
              )}

              <CardContent className="pt-3 pb-3 px-4 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate flex items-center gap-1.5">
                      <ChannelBadge channel={creative.channel} showLabel={false} />
                      {creative.name}
                    </p>
                    {creative.headline && (
                      <p className="text-xs text-muted-foreground truncate">{creative.headline}</p>
                    )}
                  </div>
                  {creative.callToAction && (
                    <Badge variant="secondary" className="ml-2 text-xs shrink-0">
                      {creative.callToAction.replace(/_/g, ' ')}
                    </Badge>
                  )}
                </div>

                {creative.bodyText && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{creative.bodyText}</p>
                )}

                {/* Meta sync status */}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {creative.metaImageHash && (
                    <span title="Synced to Meta">Meta ✓</span>
                  )}
                  {creative.parentCreativeId && (
                    <Badge variant="outline" className="text-xs">Variation</Badge>
                  )}
                  {creative.width && creative.height && (
                    <span>{creative.width}×{creative.height}</span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 pt-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowVariationForm(creative.id)
                      setVarName(`${creative.name} - Variation`)
                      setVarHeadline(creative.headline || '')
                      setVarBody(creative.bodyText || '')
                      setVarCta(creative.callToAction || 'LEARN_MORE')
                    }}
                    title="Create variation"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Variation
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(creative)}
                    disabled={deleting === creative.id}
                    className="text-destructive hover:text-destructive/80"
                    title="Delete creative"
                  >
                    {deleting === creative.id ? (
                      <Spinner className="w-3 h-3" />
                    ) : (
                      <Trash2 className="w-3 h-3" />
                    )}
                  </Button>
                </div>
              </CardContent>

              {/* Variation form for this creative */}
              {showVariationForm === creative.id && (
                <div className="border-t border-border p-4 space-y-3 bg-muted">
                  <p className="text-sm font-medium">Create Variation</p>
                  <input
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    value={varName}
                    onChange={(e) => setVarName(e.target.value)}
                    placeholder="Variation name"
                  />
                  <input
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    value={varHeadline}
                    onChange={(e) => setVarHeadline(e.target.value)}
                    placeholder="New headline"
                    maxLength={255}
                  />
                  <textarea
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    value={varBody}
                    onChange={(e) => setVarBody(e.target.value)}
                    placeholder="New body text"
                    rows={2}
                  />
                  <select
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    value={varCta}
                    onChange={(e) => setVarCta(e.target.value)}
                  >
                    {CTA_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setShowVariationForm(null)}>Cancel</Button>
                    <Button size="sm" onClick={() => handleCreateVariation(creative.id)} disabled={creating || !varName.trim()}>
                      {creating && <Spinner className="w-3 h-3 mr-1" />}
                      Create
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
